import { useState } from 'react';

import { DROPDOWN_FETCH_PAGE_SIZE, usePermissions } from '@klareospace/pharos-lib-front-utils';
import type { HandlingUnitType } from '@klareospace/pharos-lib-types';

import { useHandlingUnitTypes } from '&/features/settings/handling-unit-types/hooks';

import { useTenantDefaults } from './use-tenant-defaults';
import { useUpdateTenantDefaults } from './use-update-tenant-defaults';

// ─── Types ────────────────────────────────────────────────────────────────────

export type UseTenantDefaultsPageResult = {
  canWrite: boolean;
  currentHandlingUnitTypeId: string;
  handleCancel: () => void;
  handleHandlingUnitTypeChange: (value: string) => void;
  handleSave: () => void;
  handlingUnitTypes: HandlingUnitType[];
  isDirty: boolean;
  isLoading: boolean;
  isPending: boolean;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useTenantDefaultsPage(): UseTenantDefaultsPageResult {
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission('tenant-defaults:write');

  const { data: tenantDefaults, isLoading: isLoadingDefaults } = useTenantDefaults();
  const { data: handlingUnitTypesData, isLoading: isLoadingHandlingUnitTypes } = useHandlingUnitTypes({
    page: 1,
    pageSize: DROPDOWN_FETCH_PAGE_SIZE,
    statuses: ['active'],
  });
  const updateTenantDefaults = useUpdateTenantDefaults();

  const handlingUnitTypes = handlingUnitTypesData?.items ?? [];
  const isLoading = isLoadingDefaults || isLoadingHandlingUnitTypes;

  const [selectedHandlingUnitTypeId, setSelectedHandlingUnitTypeId] = useState<string | null>(null);

  const currentHandlingUnitTypeId = selectedHandlingUnitTypeId ?? tenantDefaults?.defaultHandlingUnitTypeId ?? '';

  const isDirty = selectedHandlingUnitTypeId !== null;

  function handleHandlingUnitTypeChange(value: string): void {
    setSelectedHandlingUnitTypeId(value === '' ? null : value);
  }

  function handleSave(): void {
    updateTenantDefaults.mutate(
      { defaultHandlingUnitTypeId: selectedHandlingUnitTypeId },
      {
        onSuccess: () => {
          setSelectedHandlingUnitTypeId(null);
        },
      },
    );
  }

  function handleCancel(): void {
    setSelectedHandlingUnitTypeId(null);
  }

  return {
    canWrite,
    currentHandlingUnitTypeId,
    handleCancel,
    handleHandlingUnitTypeChange,
    handleSave,
    handlingUnitTypes,
    isDirty,
    isLoading,
    isPending: updateTenantDefaults.isPending,
  };
}
